import { ReactElement, useState, useCallback, useMemo } from 'react';
import * as S from './PartnersDestinationPage.styles';
import Layout from '@/shared/Layout/ui/Layout';
import { ActivitiesPartnersPreview } from '@/entities/Partner/ui/ActivitiesPartnersPreview';
import { getPartner } from '@/common/service/api/Partners/Partners';
import { IPartnersItem } from '@/common/service/api/Partners/Partners.domain';
import { Container } from '@/common/ui/containers/Container';
import { URL, buildRootUrl } from '@/common/app/utils/urlsUtils';
import { CommonMeta } from '@/common/ui/meta/CommonMeta';
import { IMeta } from '@/common/domain/Meta.domain';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { removeDuplicate } from '@/common/app/utils/arrayUtils';
import { LoadMore } from '@/common/ui/loaders/LoadMore/LoadMore';
import { Destination } from '@/entities/Attractions/domain/Attractions.domain';
import { BottomButton } from '@/common/ui/buttons/BottomButton/BottomButton';
import { AnswersFAQ } from '../ActivityPage/ui/Answers/AnswersFAQ';

const CHUNK_SIZE = 10;
const DEFAULT_META_DESCRIPTION =
    "The USA's #1 booking website for items, tours, and things to do. Browse reviews, deals, photos and more from over 1,000+ experiences. Low price guarantee on every booking.";

export type PartnersListDetailProps = {
    meta: IMeta;
    items: IPartnersItem[];
    currentDestination: Destination;
    images: string[];
    breadcrumbs: IBreadcrumbsItem[];
};

const PartnersListDetails = ({
    meta,
    items,
    currentDestination,
    images,
    breadcrumbs,
}: PartnersListDetailProps): ReactElement => {
    const [isLoadMore, setIsLoadMore] = useState(false);
    const [isNotMore, setIsNotMore] = useState(items.length < CHUNK_SIZE);
    const [partnerDetails, setPartnerDetails] = useState(items);

    const getNextPartnerDetails = useCallback(async () => {
        const { items: newPartnerDetails } = await getPartner({
            destination_id: currentDestination.id,
            offset: partnerDetails.length,
        });

        if (!newPartnerDetails) {
            return;
        }

        if (newPartnerDetails.length < CHUNK_SIZE) {
            setIsNotMore(true);
        }

        const updatedList = newPartnerDetails.map((partnerItem) => {
            return {
                ...partnerItem,
                url: `/partners/${currentDestination.state.code}/${currentDestination.slug}/${partnerItem.slug}/${partnerItem.id}/`.toLowerCase(),
            };
        });

        setPartnerDetails((prev) => removeDuplicate([...prev, ...updatedList], 'id'));
        setIsLoadMore(false);
    }, [
        currentDestination.id,
        partnerDetails,
        currentDestination.slug,
        currentDestination.state.code,
    ]);

    const title = `${currentDestination.name}, ${currentDestination.state.name}`;

    const onScrollPage = useCallback(() => {
        if (
            !partnerDetails.length ||
            partnerDetails.length < CHUNK_SIZE ||
            isLoadMore ||
            isNotMore
        ) {
            return;
        }

        setIsLoadMore(true);

        getNextPartnerDetails();
    }, [partnerDetails.length, isLoadMore, isNotMore, getNextPartnerDetails]);

    const firstTenDestinations = partnerDetails.slice(0, CHUNK_SIZE);

    const schemaItemList = useMemo(() => {
        const itemListElement = firstTenDestinations?.map((v, i) => {
            return {
                '@type': 'ListItem',
                'position': ++i,
                'name': v.title,
                'url': buildRootUrl(v.url),
                'description': v.description,
                'image': `${URL.image}/activity/${v.id}/480x240/${v.slug}.jpg`,
            };
        });

        const name = `${currentDestination.name}, ${currentDestination.state.code}`.toUpperCase();

        return JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'ItemList',
            'name': `TOUR COMPANIES IN ${name}`,
            'description': meta?.description || '',
            'itemListOrder': 'http://schema.org/ItemListOrderAscending',
            itemListElement,
        });
    }, [
        firstTenDestinations,
        meta.description,
        currentDestination.name,
        currentDestination.state.code,
    ]);
    const partnerLink = buildRootUrl(
        `/partners/${currentDestination.state.code}/${currentDestination.slug}/`.toLowerCase()
    );
    const imgSrc = `${URL.image}/destination/${currentDestination.id}/1080x1080/${currentDestination.slug}.jpg`;
    const pageUrl = buildRootUrl(partnerLink);

    return (
        <Layout images={images} TitleName={title} breadcrumbs={breadcrumbs}>
            <CommonMeta
                title={meta.title}
                description={meta.description || DEFAULT_META_DESCRIPTION}
                imageSrc={imgSrc}
                slug={partnerLink}
                canonical={partnerLink}
                structuredData={[schemaItemList]}
                ogUrl={partnerLink}
            />
            <Container data-test-id="Partners_destination">
                <S.ListTitle>{currentDestination.name} Tour Companies</S.ListTitle>
                <S.ListWrapper>
                    {partnerDetails.map((item) => {
                        return <ActivitiesPartnersPreview key={item.id} {...item} />;
                    })}
                    <S.LoadMoreTrigger />
                </S.ListWrapper>
                {!isNotMore && !isLoadMore && (
                    <BottomButton title="Show more" onClick={onScrollPage} />
                )}
                <LoadMore isLoading={isLoadMore} />
            </Container>
            <AnswersFAQ
                title={title}
                url={pageUrl}
                image={imgSrc}
                id={`partners_d${currentDestination.id}`}
            />
        </Layout>
    );
};

export { PartnersListDetails };
export default PartnersListDetails;
